import React, { useState, useEffect, useCallback } from 'react'
import { Image, TextInput, Alert, Platform, FlatList, Pressable, useWindowDimensions } from 'react-native'
import {
  ArrowLeft, ExternalLink, Facebook, Instagram, Video, Check, X,
  ChevronDown, ChevronUp,
} from 'lucide-react-native'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RouteProp } from '@react-navigation/native'
import { RootStackParamList } from '../types/navigation'
import { api } from '../lib/api'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { useToast } from '../components/Toast'
import {
  Button,
  ButtonText,
  Card,
  Divider,
  ListItem,
  Spinner,
  StatusBadge,
  Text,
  Theme,
  TopBar,
  IconButton,
  XStack,
  YStack,
  ScrollView,
} from '@fliisi/pollen'

interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'SocialAccounts'>
  route: RouteProp<RootStackParamList, 'SocialAccounts'>
}

interface SocialPlatform {
  key: string
  label: string
  icon: React.ComponentType<{ size: number; color: string }>
  color: string
}

const PLATFORMS: SocialPlatform[] = [
  { key: 'facebook', label: 'Facebook', icon: Facebook, color: '#1877F2' },
  { key: 'instagram', label: 'Instagram', icon: Instagram, color: '#E1306C' },
  { key: 'tiktok', label: 'TikTok', icon: Video, color: '#000000' },
]

type TabKey = 'accounts' | 'posts' | 'create'

interface ConnectedPlatform {
  platform: string
  username: string | null
}

interface SocialPost {
  id: string
  store_id: string
  platforms: string[]
  caption: string
  media_urls: string[]
  product_ids: string[]
  status: string
  error_message: string | null
  created_at: string
}

interface Product {
  id: string
  title: string
  title_fi: string | null
  price: number
  status: string
  product_images: { image_url: string }[]
}

export default function SocialAccountsScreen({ navigation, route }: Props) {
  const { storeId, tab: initialTab } = route.params
  const toast = useToast()
  const { user } = useAuth()
  const { width: windowWidth } = useWindowDimensions()

  const [activeTab, setActiveTab] = useState<TabKey>(initialTab || 'accounts')

  // --- Accounts tab state ---
  const [connectedPlatforms, setConnectedPlatforms] = useState<ConnectedPlatform[]>([])
  const [disconnectedPlatforms, setDisconnectedPlatforms] = useState<string[]>([])
  const [loadingProfiles, setLoadingProfiles] = useState(true)
  const [connecting, setConnecting] = useState(false)
  const [disconnecting, setDisconnecting] = useState<string | null>(null)

  // --- Posts tab state ---
  const [posts, setPosts] = useState<SocialPost[]>([])
  const [postProducts, setPostProducts] = useState<Record<string, Product>>({})
  const [loadingPosts, setLoadingPosts] = useState(false)
  const [expandedPost, setExpandedPost] = useState<string | null>(null)

  // --- Create tab state ---
  const [createStep, setCreateStep] = useState<1 | 2>(1)
  const [products, setProducts] = useState<Product[]>([])
  const [loadingProducts, setLoadingProducts] = useState(false)
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(new Set())
  const [caption, setCaption] = useState('')
  const [selectedPlatforms, setSelectedPlatforms] = useState<Set<string>>(new Set())
  const [posting, setPosting] = useState(false)
  const [storeName, setStoreName] = useState('')

  // --- Load profiles on mount ---
  const loadProfiles = useCallback(async () => {
    setLoadingProfiles(true)
    try {
      const result = await api.socialProfiles.fetch({ profileKey: storeId })
      setConnectedPlatforms(result.connected)
      setDisconnectedPlatforms(result.disconnected)
    } catch (error: any) {
      console.error('Failed to load profiles:', error)
      // Fallback: show all as disconnected
      setConnectedPlatforms([])
      setDisconnectedPlatforms(['facebook', 'instagram', 'tiktok'])
    } finally {
      setLoadingProfiles(false)
    }
  }, [storeId])

  useEffect(() => {
    loadProfiles()
    loadStoreName()
  }, [loadProfiles])

  const loadStoreName = async () => {
    const { data } = await supabase.from('flea_markets').select('name').eq('id', storeId).single()
    if (data) setStoreName(data.name)
  }

  // --- Load posts when tab changes ---
  useEffect(() => {
    if (activeTab === 'posts') loadPosts()
    if (activeTab === 'create' && products.length === 0) loadAvailableProducts()
  }, [activeTab])

  const loadPosts = async () => {
    setLoadingPosts(true)
    try {
      const { data, error } = await supabase
        .from('social_posts')
        .select('*')
        .eq('store_id', storeId)
        .order('created_at', { ascending: false })
        .limit(50)

      if (error) throw error
      setPosts(data || [])

      // Fetch product thumbnails
      const allProductIds = new Set<string>()
      for (const post of data || []) {
        for (const pid of post.product_ids || []) {
          allProductIds.add(pid)
        }
      }

      if (allProductIds.size > 0) {
        const { data: prods } = await supabase
          .from('products')
          .select('id, title, title_fi, price, status, product_images(image_url)')
          .in('id', Array.from(allProductIds))

        if (prods) {
          const map: Record<string, Product> = {}
          for (const p of prods) map[p.id] = p as Product
          setPostProducts(map)
        }
      }
    } catch (error) {
      console.error('Failed to load posts:', error)
    } finally {
      setLoadingPosts(false)
    }
  }

  const loadAvailableProducts = async () => {
    setLoadingProducts(true)
    try {
      const { data, error } = await supabase
        .from('products')
        .select('id, title, title_fi, price, status, product_images(image_url)')
        .eq('flea_market_id', storeId)
        .eq('status', 'for_sale')
        .order('created_at', { ascending: false })
        .limit(100)

      if (error) throw error
      setProducts((data || []) as Product[])
    } catch (error) {
      console.error('Failed to load products:', error)
    } finally {
      setLoadingProducts(false)
    }
  }

  // --- Accounts tab actions ---
  const openConnectFlow = async () => {
    setConnecting(true)
    try {
      const result = await api.socialConnect.fetch({ profileTitle: storeId })
      if (result.url) {
        if (Platform.OS === 'web') {
          window.open(result.url, '_blank')
        } else {
          const { Linking } = require('react-native')
          await Linking.openURL(result.url)
        }
        toast.success('Yhdistaminen', 'Yhdista tilisi avautuneessa selaimessa ja palaa takaisin.')
      }
    } catch (error: any) {
      toast.error('Virhe', error.message || 'Tilien yhdistaminen epaonnistui')
    } finally {
      setConnecting(false)
    }
  }

  const handleDisconnect = (platform: string) => {
    const doDisconnect = async () => {
      setDisconnecting(platform)
      try {
        await api.socialDisconnect.fetch({
          profileKey: storeId,
          platform: platform as 'facebook' | 'instagram' | 'tiktok',
        })
        toast.success('Irrotettu', `${platform} irrotettu onnistuneesti`)
        await loadProfiles()
      } catch (error: any) {
        toast.error('Virhe', error.message || 'Irrottaminen epaonnistui')
      } finally {
        setDisconnecting(null)
      }
    }

    if (Platform.OS === 'web') {
      if (window.confirm(`Haluatko varmasti irrottaa ${platform}-tilin?`)) {
        doDisconnect()
      }
    } else {
      Alert.alert('Irroita tili', `Haluatko varmasti irrottaa ${platform}-tilin?`, [
        { text: 'Peruuta', style: 'cancel' },
        { text: 'Irroita', style: 'destructive', onPress: doDisconnect },
      ])
    }
  }

  // --- Create tab actions ---
  const toggleProduct = (productId: string) => {
    setSelectedProductIds(prev => {
      const next = new Set(prev)
      if (next.has(productId)) {
        next.delete(productId)
      } else if (next.size < 10) {
        next.add(productId)
      }
      return next
    })
  }

  const goToComposer = () => {
    // Pre-fill caption
    const selectedProducts = products.filter(p => selectedProductIds.has(p.id))
    const productLines = selectedProducts.map(p => {
      const title = p.title_fi || p.title
      return `${title} ${p.price.toFixed(2)} €`
    }).join(' | ')

    const defaultCaption = `${productLines} — ${storeName}\n\n#kirpputori #kirppis #secondhand #kierrätys #vintage #löytö #fliisi`
    setCaption(defaultCaption)

    // Auto-select connected platforms
    const connectedKeys = new Set(connectedPlatforms.map(p => p.platform))
    setSelectedPlatforms(connectedKeys)

    setCreateStep(2)
  }

  const removeProduct = (productId: string) => {
    setSelectedProductIds(prev => {
      const next = new Set(prev)
      next.delete(productId)
      return next
    })
    if (selectedProductIds.size <= 1) {
      setCreateStep(1)
    }
  }

  const togglePlatform = (platform: string) => {
    const isConnected = connectedPlatforms.some(p => p.platform === platform)
    if (!isConnected) return

    setSelectedPlatforms(prev => {
      const next = new Set(prev)
      if (next.has(platform)) {
        next.delete(platform)
      } else {
        next.add(platform)
      }
      return next
    })
  }

  const publishPost = async () => {
    if (selectedPlatforms.size === 0) {
      toast.error('Virhe', 'Valitse vahintaan yksi kanava')
      return
    }

    const selectedProducts = products.filter(p => selectedProductIds.has(p.id))
    const imageUrls = selectedProducts
      .flatMap(p => p.product_images?.map(img => img.image_url) || [])
      .filter(Boolean)
      .slice(0, 10)

    if (imageUrls.length === 0) {
      toast.error('Virhe', 'Valituilla tuotteilla ei ole kuvia')
      return
    }

    setPosting(true)
    try {
      await api.socialPost.fetch({
        title: selectedProducts[0]?.title_fi || selectedProducts[0]?.title || 'Tuote',
        price: selectedProducts[0]?.price || 0,
        storeName,
        imageUrls,
        platforms: Array.from(selectedPlatforms) as ('facebook' | 'instagram' | 'tiktok')[],
        profileKey: storeId,
        storeId,
        userId: user?.id,
        productIds: Array.from(selectedProductIds),
        caption,
      })

      toast.success('Julkaistu!', 'Julkaisu onnistui')
      setSelectedProductIds(new Set())
      setCreateStep(1)
      setActiveTab('posts')
      loadPosts()
    } catch (error: any) {
      toast.error('Virhe', error.message || 'Julkaisu epaonnistui')
    } finally {
      setPosting(false)
    }
  }

  // --- Helpers ---
  const isConnected = (platformKey: string) =>
    connectedPlatforms.some(p => p.platform === platformKey)

  const getUsername = (platformKey: string) =>
    connectedPlatforms.find(p => p.platform === platformKey)?.username

  const formatRelativeDate = (dateStr: string) => {
    const now = new Date()
    const date = new Date(dateStr)
    const diffMs = now.getTime() - date.getTime()
    const diffMin = Math.floor(diffMs / 60000)
    const diffH = Math.floor(diffMin / 60)
    const diffD = Math.floor(diffH / 24)

    if (diffMin < 1) return 'Juuri nyt'
    if (diffMin < 60) return `${diffMin} min sitten`
    if (diffH < 24) return `${diffH} t sitten`
    if (diffD < 7) return `${diffD} pv sitten`
    return date.toLocaleDateString('fi-FI')
  }

  const getPlatformIcon = (key: string) => {
    const p = PLATFORMS.find(pl => pl.key === key)
    if (!p) return null
    const Icon = p.icon
    return <Icon size={14} color={p.color} />
  }

  // ====================== RENDER ======================

  const renderTabBar = () => (
    <XStack px="$md" py="$sm" gap="$sm">
      {([
        { key: 'accounts' as TabKey, label: 'Tilit' },
        { key: 'posts' as TabKey, label: 'Julkaisut' },
        { key: 'create' as TabKey, label: 'Luo julkaisu' },
      ]).map(tab => (
        <Pressable
          key={tab.key}
          onPress={() => setActiveTab(tab.key)}
          style={{ flex: 1 }}
        >
          <YStack
            bg={activeTab === tab.key ? '$colorPrimary' : '$backgroundCard'}
            py={8} br="$full" ai="center"
            borderWidth={1}
            borderColor={activeTab === tab.key ? '$colorPrimary' : '$borderColor'}
          >
            <Text
              fontSize={13} fontWeight="600"
              color={activeTab === tab.key ? '#fff' : '$color'}
            >
              {tab.label}
            </Text>
          </YStack>
        </Pressable>
      ))}
    </XStack>
  )

  const renderAccountsTab = () => {
    if (loadingProfiles) {
      return <YStack f={1} jc="center" ai="center" py="$xl"><Spinner size="small" /></YStack>
    }

    return (
      <YStack px="$md" gap="$md">
        <Card variant="flat">
          <YStack gap="$sm">
            <Text preset="h3">Yhdistetyt tilit</Text>
            <Text preset="body" tone="secondary">
              Hallinnoi myymälasi sosiaalisen median tilejä.
            </Text>
          </YStack>
        </Card>

        <Card variant="flat">
          {PLATFORMS.map((platform, i) => {
            const Icon = platform.icon
            const connected = isConnected(platform.key)
            const username = getUsername(platform.key)
            const isDisconnecting = disconnecting === platform.key

            return (
              <React.Fragment key={platform.key}>
                {i > 0 && <Divider />}
                <ListItem
                  icon={
                    <YStack
                      w={40} h={40} br={10}
                      bg={`${platform.color}15`}
                      jc="center" ai="center"
                    >
                      <Icon size={20} color={platform.color} />
                    </YStack>
                  }
                  title={platform.label}
                  subtitle={connected ? (username || 'Yhdistetty') : 'Ei yhdistetty'}
                  right={
                    connected ? (
                      <XStack ai="center" gap="$sm">
                        <StatusBadge variant="success">Yhdistetty</StatusBadge>
                        <Pressable onPress={() => handleDisconnect(platform.key)} disabled={isDisconnecting}>
                          <YStack
                            w={32} h={32} br="$full"
                            bg="rgba(211,47,47,0.1)" jc="center" ai="center"
                          >
                            {isDisconnecting ? (
                              <Spinner size="small" />
                            ) : (
                              <X size={14} color="#D32F2F" />
                            )}
                          </YStack>
                        </Pressable>
                      </XStack>
                    ) : (
                      <Pressable onPress={openConnectFlow} disabled={connecting}>
                        <XStack bg={`${platform.color}15`} px={10} py={4} br="$full">
                          <Text fontSize={12} color={platform.color} fontWeight="600">
                            Yhdistä
                          </Text>
                        </XStack>
                      </Pressable>
                    )
                  }
                />
              </React.Fragment>
            )
          })}
        </Card>

        <Text preset="caption" tone="muted" textAlign="center" px="$md">
          Yhdistaminen tapahtuu turvallisesti Ayrsharen kautta.
          Fliisi ei tallenna salasanojasi.
        </Text>
      </YStack>
    )
  }

  const renderPostsTab = () => {
    if (loadingPosts) {
      return <YStack f={1} jc="center" ai="center" py="$xl"><Spinner size="small" /></YStack>
    }

    if (posts.length === 0) {
      return (
        <YStack f={1} jc="center" ai="center" py="$xl" gap="$md">
          <Text preset="body" tone="secondary">Et ole viela julkaissut mitaan</Text>
          <Button variant="primary" onPress={() => setActiveTab('create')}>
            <ButtonText>Luo ensimmainen julkaisu</ButtonText>
          </Button>
        </YStack>
      )
    }

    return (
      <YStack px="$md" gap="$sm">
        {posts.map(post => {
          const isExpanded = expandedPost === post.id
          const firstProductId = post.product_ids?.[0]
          const firstProduct = firstProductId ? postProducts[firstProductId] : null
          const thumbnailUrl = firstProduct?.product_images?.[0]?.image_url

          return (
            <Pressable key={post.id} onPress={() => setExpandedPost(isExpanded ? null : post.id)}>
              <Card variant="flat">
                <XStack ai="center" gap="$sm">
                  {thumbnailUrl ? (
                    <Image source={{ uri: thumbnailUrl }} style={{ width: 40, height: 40, borderRadius: 8 }} />
                  ) : (
                    <YStack w={40} h={40} br="$md" bg="$backgroundSecondary" jc="center" ai="center">
                      <ExternalLink size={16} color="#858B80" />
                    </YStack>
                  )}
                  <YStack f={1} gap={2}>
                    <Text fontSize={14} numberOfLines={2} color="$color">{post.caption}</Text>
                    <XStack ai="center" gap="$xs">
                      {post.platforms.map(p => (
                        <React.Fragment key={p}>{getPlatformIcon(p)}</React.Fragment>
                      ))}
                      <Text preset="caption" tone="muted" ml="$xs">{formatRelativeDate(post.created_at)}</Text>
                    </XStack>
                  </YStack>
                  {isExpanded ? <ChevronUp size={16} color="#858B80" /> : <ChevronDown size={16} color="#858B80" />}
                </XStack>

                {isExpanded && (
                  <YStack mt="$sm" gap="$sm">
                    <Divider />
                    <Text preset="body" color="$color">{post.caption}</Text>

                    {post.product_ids.length > 0 && (
                      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                        <XStack gap="$xs">
                          {post.product_ids.map(pid => {
                            const prod = postProducts[pid]
                            const imgUrl = prod?.product_images?.[0]?.image_url
                            return imgUrl ? (
                              <Image key={pid} source={{ uri: imgUrl }} style={{ width: 48, height: 48, borderRadius: 6 }} />
                            ) : (
                              <YStack key={pid} w={48} h={48} br={6} bg="$backgroundSecondary" jc="center" ai="center">
                                <Text fontSize={10} tone="muted">?</Text>
                              </YStack>
                            )
                          })}
                        </XStack>
                      </ScrollView>
                    )}

                    <XStack ai="center" gap="$sm">
                      <StatusBadge variant={post.status === 'posted' ? 'success' : 'error'}>
                        {post.status === 'posted' ? 'Julkaistu' : 'Epäonnistui'}
                      </StatusBadge>
                    </XStack>

                    {post.status === 'failed' && post.error_message && (
                      <Text preset="caption" color="$colorError">{post.error_message}</Text>
                    )}
                  </YStack>
                )}
              </Card>
            </Pressable>
          )
        })}
      </YStack>
    )
  }

  const renderCreateTab = () => {
    if (createStep === 2) return renderComposer()
    return renderProductGrid()
  }

  const renderProductGrid = () => {
    if (loadingProducts) {
      return <YStack f={1} jc="center" ai="center" py="$xl"><Spinner size="small" /></YStack>
    }

    // Responsive columns: 2 on mobile, 3 at 600px+, 4 at 900px+
    const numColumns = windowWidth >= 900 ? 4 : windowWidth >= 600 ? 3 : 2
    const horizontalPadding = 12
    const gap = 8
    const availableWidth = Math.min(windowWidth, 768) - horizontalPadding * 2
    const cardWidth = (availableWidth - gap * (numColumns - 1)) / numColumns

    return (
      <YStack f={1}>
        <XStack px="$md" py="$sm" jc="space-between" ai="center">
          <Text preset="h3">Valitse tuotteet (1-10)</Text>
          <Text preset="caption" tone="secondary">{selectedProductIds.size}/10 valittu</Text>
        </XStack>

        <FlatList
          key={`grid-${numColumns}`}
          data={products}
          numColumns={numColumns}
          keyExtractor={item => item.id}
          contentContainerStyle={{ paddingHorizontal: horizontalPadding, paddingBottom: 80, maxWidth: 768, alignSelf: 'center' as const, width: '100%' }}
          columnWrapperStyle={{ gap }}
          ItemSeparatorComponent={() => <YStack h={gap} />}
          renderItem={({ item }) => {
            const isSelected = selectedProductIds.has(item.id)
            const imgUrl = item.product_images?.[0]?.image_url

            return (
              <Pressable onPress={() => toggleProduct(item.id)} style={{ width: cardWidth, maxWidth: 160 }}>
                <YStack
                  br="$md" overflow="hidden"
                  borderWidth={2}
                  borderColor={isSelected ? '$colorPrimary' : '$borderColor'}
                  bg="$backgroundCard"
                >
                  <YStack position="relative">
                    {imgUrl ? (
                      <Image source={{ uri: imgUrl }} style={{ width: '100%', aspectRatio: 1, borderTopLeftRadius: 8, borderTopRightRadius: 8 }} resizeMode="cover" />
                    ) : (
                      <YStack w="100%" aspectRatio={1} bg="$backgroundSecondary" jc="center" ai="center">
                        <Text tone="muted">Ei kuvaa</Text>
                      </YStack>
                    )}
                    {/* Checkbox overlay */}
                    <YStack
                      position="absolute" top={6} right={6}
                      w={24} h={24} br="$full"
                      bg={isSelected ? '$colorPrimary' : 'rgba(255,255,255,0.8)'}
                      borderWidth={isSelected ? 0 : 2}
                      borderColor="$borderColor"
                      jc="center" ai="center"
                    >
                      {isSelected && <Check size={14} color="#fff" />}
                    </YStack>
                  </YStack>
                  <YStack px="$sm" py="$xs">
                    <Text fontSize={13} numberOfLines={1} color="$color">{item.title_fi || item.title}</Text>
                    <Text fontSize={13} fontWeight="600" color="$colorPrimary">{item.price.toFixed(2)} €</Text>
                  </YStack>
                </YStack>
              </Pressable>
            )
          }}
        />

        {selectedProductIds.size > 0 && (
          <YStack
            position="absolute" bottom={0} left={0} right={0}
            px="$md" py="$sm" bg="$background"
            borderTopWidth={1} borderTopColor="$borderColor"
          >
            <Button variant="primary" onPress={goToComposer}>
              <ButtonText>Jatka ({selectedProductIds.size})</ButtonText>
            </Button>
          </YStack>
        )}
      </YStack>
    )
  }

  const renderComposer = () => {
    const selectedProducts = products.filter(p => selectedProductIds.has(p.id))

    return (
      <ScrollView f={1}>
        <YStack px="$md" gap="$md" pb="$xl">
          {/* Back link */}
          <Pressable onPress={() => setCreateStep(1)}>
            <XStack ai="center" gap="$xs">
              <ArrowLeft size={16} color="$colorPrimary" />
              <Text color="$colorPrimary" fontSize={14}>Takaisin tuotevalintaan</Text>
            </XStack>
          </Pressable>

          {/* Selected products */}
          <YStack gap="$xs">
            <Text preset="label" tone="secondary">Valitut tuotteet</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              <XStack gap="$sm">
                {selectedProducts.map(p => {
                  const imgUrl = p.product_images?.[0]?.image_url
                  return (
                    <YStack key={p.id} position="relative">
                      {imgUrl ? (
                        <Image source={{ uri: imgUrl }} style={{ width: 60, height: 60, borderRadius: 8 }} />
                      ) : (
                        <YStack w={60} h={60} br="$md" bg="$backgroundSecondary" jc="center" ai="center">
                          <Text fontSize={10} tone="muted">?</Text>
                        </YStack>
                      )}
                      <Pressable
                        onPress={() => removeProduct(p.id)}
                        style={{ position: 'absolute', top: -4, right: -4 }}
                      >
                        <YStack w={20} h={20} br="$full" bg="rgba(211,47,47,0.9)" jc="center" ai="center">
                          <X size={12} color="#fff" />
                        </YStack>
                      </Pressable>
                    </YStack>
                  )
                })}
              </XStack>
            </ScrollView>
          </YStack>

          {/* Caption */}
          <YStack gap="$xs">
            <Text preset="label" tone="secondary">Kuvateksti</Text>
            <TextInput
              multiline
              numberOfLines={6}
              value={caption}
              onChangeText={setCaption}
              style={{
                backgroundColor: 'var(--backgroundCard)',
                borderRadius: 14,
                padding: 14,
                fontSize: 14,
                borderWidth: 1.5,
                borderColor: 'var(--borderColor)',
                color: 'var(--color)',
                minHeight: 120,
                textAlignVertical: 'top',
              }}
              placeholderTextColor="var(--colorSecondary)"
            />
          </YStack>

          {/* Platform toggles */}
          <YStack gap="$xs">
            <Text preset="label" tone="secondary">Kanavat</Text>
            <XStack gap={8} flexWrap="wrap">
              {PLATFORMS.map(platform => {
                const Icon = platform.icon
                const connected = isConnected(platform.key)
                const selected = selectedPlatforms.has(platform.key)

                return (
                  <Pressable
                    key={platform.key}
                    onPress={() => togglePlatform(platform.key)}
                    disabled={!connected}
                    style={{ opacity: connected ? 1 : 0.4 }}
                  >
                    <XStack
                      bg={selected ? platform.color : '$backgroundCard'}
                      borderWidth={1.5}
                      borderColor={selected ? platform.color : '$borderColor'}
                      px={14} py={8} br={16}
                      ai="center" gap={6}
                    >
                      <Icon size={16} color={selected ? '#fff' : platform.color} />
                      <YStack>
                        <Text color={selected ? '#fff' : '$color'} fontSize={13} fontWeight="600">
                          {platform.label}
                        </Text>
                        {!connected && (
                          <Text fontSize={10} color="$colorSecondary">Yhdista ensin</Text>
                        )}
                      </YStack>
                      {selected && <Check size={14} color="#fff" />}
                    </XStack>
                  </Pressable>
                )
              })}
            </XStack>
          </YStack>

          {/* Publish button */}
          <Button
            variant="primary"
            onPress={publishPost}
            disabled={posting || selectedPlatforms.size === 0}
          >
            {posting ? (
              <Spinner size="small" color="#fff" />
            ) : (
              <ButtonText>Julkaise nyt</ButtonText>
            )}
          </Button>
        </YStack>
      </ScrollView>
    )
  }

  return (
    <Theme name="brick">
      <YStack f={1} bg="$background">
        <TopBar
          title="Sosiaalinen media"
          left={
            <IconButton variant="ghost" onPress={() => navigation.goBack()}>
              <ArrowLeft size={20} color="$colorPrimary" />
            </IconButton>
          }
        />
        {renderTabBar()}
        <YStack f={1}>
          {activeTab === 'accounts' && (
            <ScrollView f={1}>{renderAccountsTab()}</ScrollView>
          )}
          {activeTab === 'posts' && (
            <ScrollView f={1}>{renderPostsTab()}</ScrollView>
          )}
          {activeTab === 'create' && renderCreateTab()}
        </YStack>
      </YStack>
    </Theme>
  )
}
